import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  clampMetricDateRange,
  getMetricEffectiveStartDate,
  getPreviousMonthToDateRange,
} from '@/common/utils/metric-effective-period';
import { getOperationAnalysisSubjectsQueryKey } from '@/common/utils/operation-analysis-scope';

export { buildOperationEntityTree as buildAnalysisSubjectTree } from '@/common/utils/operation-analysis-scope';

dayjs.extend(customParseFormat);

export const OPERATION_ANALYSIS_PAGE_SIZE = 10;
export const OPERATION_ANALYSIS_BULK_PAGE_SIZE = 1000;
const DATE_FORMAT = 'YYYY-MM-DD';
const OPERATION_ANALYSIS_SORT_FIELDS = new Set([
  'storeIncomeAmount',
  'stallIncomeAmount',
  'expenseRate',
]);
const OPERATION_ANALYSIS_SORT_ORDERS = new Set(['ascend', 'descend']);
const OPERATION_ANALYSIS_GRANULARITIES = new Set(['day', 'week']);
export const DEFAULT_OPERATION_ANALYSIS_GRANULARITY = 'week';
export const DEFAULT_OPERATION_ANALYSIS_SORT_FIELD = 'storeIncomeAmount';
export const DEFAULT_OPERATION_ANALYSIS_SORT_ORDER = 'descend';
const amountFormatter = new Intl.NumberFormat('zh-CN', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});
const profitRateFormatter = new Intl.NumberFormat('zh-CN', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});
const storeNameCollator = new Intl.Collator('zh-CN', {
  numeric: true,
  sensitivity: 'base',
});

const getQueryValue = (value) => (Array.isArray(value) ? value.at(-1) : value);

export const getAnalysisRankPage = (rankPage) => {
  const page = Number(getQueryValue(rankPage));
  return Number.isSafeInteger(page) && page > 0 ? page : 1;
};

export const getAnalysisGranularity = (granularity) => {
  const requestedGranularity = getQueryValue(granularity);
  return OPERATION_ANALYSIS_GRANULARITIES.has(requestedGranularity)
    ? requestedGranularity
    : DEFAULT_OPERATION_ANALYSIS_GRANULARITY;
};

export const getAnalysisSort = (sortField, sortOrder) => {
  const requestedField = getQueryValue(sortField);
  const requestedOrder = getQueryValue(sortOrder);
  const hasSupportedField = OPERATION_ANALYSIS_SORT_FIELDS.has(requestedField);
  return {
    sortField: hasSupportedField ? requestedField : DEFAULT_OPERATION_ANALYSIS_SORT_FIELD,
    sortOrder:
      hasSupportedField && OPERATION_ANALYSIS_SORT_ORDERS.has(requestedOrder)
        ? requestedOrder
        : DEFAULT_OPERATION_ANALYSIS_SORT_ORDER,
  };
};

export const getAnalysisMaxRankPage = (total) => {
  const normalizedTotal = Number(total);
  return Math.max(
    1,
    Math.ceil(
      (Number.isFinite(normalizedTotal) && normalizedTotal > 0 ? normalizedTotal : 0) /
        OPERATION_ANALYSIS_PAGE_SIZE,
    ),
  );
};

export const getAnalysisRankPageCorrection = ({ isSuccess, rankPage, total }) => {
  if (!isSuccess) {
    return undefined;
  }
  const maxRankPage = getAnalysisMaxRankPage(total);
  return rankPage > maxRankPage ? maxRankPage : undefined;
};

export const getLocalAnalysisStoreRank = ({ current, data, sortField, sortOrder, total }) => {
  const normalizedTotal = Number(total);
  if (!Number.isFinite(normalizedTotal) || normalizedTotal > data.length) {
    return null;
  }
  const sorted = [...data].sort((left, right) => {
    const leftValue = left[sortField];
    const rightValue = right[sortField];
    let valueOrder = 0;
    if (leftValue === null || leftValue === undefined) {
      valueOrder = rightValue === null || rightValue === undefined ? 0 : 1;
    } else if (rightValue === null || rightValue === undefined) {
      valueOrder = -1;
    } else {
      valueOrder = (leftValue - rightValue) * (sortOrder === 'ascend' ? 1 : -1);
    }
    return (
      valueOrder ||
      storeNameCollator.compare(left.name, right.name) ||
      String(left.id).localeCompare(String(right.id))
    );
  });
  const page = getAnalysisRankPage(current);
  const offset = (page - 1) * OPERATION_ANALYSIS_PAGE_SIZE;
  return {
    data: sorted.slice(offset, offset + OPERATION_ANALYSIS_PAGE_SIZE),
    meta: { page, pageSize: OPERATION_ANALYSIS_PAGE_SIZE, total: normalizedTotal },
  };
};

export const getAnalysisSubjectsQueryKey = getOperationAnalysisSubjectsQueryKey;

export const getAnalysisDashboardQueryKey = (organizationId, identityKey, params) => [
  'operation-analysis-dashboard',
  organizationId,
  identityKey,
  params,
];

export const getAnalysisDashboardErrorMessage = ({
  dashboardError,
  periodError,
  queryEnabled,
  subjectsError,
}) => {
  if (subjectsError) {
    return '经营主体加载失败，请刷新重试';
  }
  if (periodError) {
    return '指标有效周期加载失败，请刷新重试';
  }
  return dashboardError && queryEnabled ? '经营分析加载失败，请刷新重试' : undefined;
};

export const getAnalysisDateRange = (
  startDate,
  endDate,
  today = dayjs(),
  effectiveStartAt = null,
) => {
  const effectiveStartDate = getMetricEffectiveStartDate(effectiveStartAt);
  const [previousMonthStart, currentBusinessDate] = getPreviousMonthToDateRange(today);
  const daysSinceMonday = (previousMonthStart.day() + 6) % 7;
  const defaultRange = clampMetricDateRange(
    [previousMonthStart.subtract(daysSinceMonday, 'day'), currentBusinessDate.subtract(1, 'day')],
    effectiveStartDate,
  );
  const startValue = getQueryValue(startDate);
  const endValue = getQueryValue(endDate);
  let range = defaultRange;
  if (!startValue || !endValue) {
    return range;
  }
  const start = dayjs(startValue, DATE_FORMAT, true);
  const end = dayjs(endValue, DATE_FORMAT, true);
  if (start.isValid() && end.isValid() && !start.isAfter(end)) {
    range = [start, end];
  }
  return clampMetricDateRange(range, effectiveStartDate);
};

export const getAnalysisQueryPatch = (query, today = dayjs(), effectiveStartAt = null) => {
  const patch = {};
  const granularity = getAnalysisGranularity(query.granularity);
  const rankPage = getAnalysisRankPage(query.rankPage);
  const { sortField, sortOrder } = getAnalysisSort(query.sortField, query.sortOrder);
  const [start, end] = getAnalysisDateRange(
    query.startDate,
    query.endDate,
    today,
    effectiveStartAt,
  );
  const startDate = start.format(DATE_FORMAT);
  const endDate = end.format(DATE_FORMAT);
  if (getQueryValue(query.granularity) !== granularity) {
    patch.granularity = granularity;
  }
  if (String(getQueryValue(query.rankPage) || '') !== String(rankPage)) {
    patch.rankPage = rankPage;
  }
  if (getQueryValue(query.sortField) !== sortField) {
    patch.sortField = sortField;
  }
  if (getQueryValue(query.sortOrder) !== sortOrder) {
    patch.sortOrder = sortOrder;
  }
  if (getQueryValue(query.startDate) !== startDate) {
    patch.startDate = startDate;
  }
  if (getQueryValue(query.endDate) !== endDate) {
    patch.endDate = endDate;
  }
  return patch;
};

const getFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const formatAnalysisAmount = (value) => {
  const number = getFiniteNumber(value);
  return number === null ? '-' : amountFormatter.format(number);
};

export const formatAnalysisProfitRate = (value) => {
  const number = getFiniteNumber(value);
  return number === null ? '-' : profitRateFormatter.format(number);
};
